import React, { useMemo } from "react";
import { formatAmount } from "../../utils/format";
import { PART_TYPES, getPartTypeSectionTitle } from "../../utils/partTypes";
import ProductRow from "../../components/ProductRow";
import { isPeintureDesignation } from "../../utils/paint";

const isPeintureLine = (detail) => isPeintureDesignation(detail.designation || detail.product?.name);
const isServiceLine = (detail) => detail.product?.family?.familyType === "SERVICE";

const sumTotals = (lines) => lines.reduce(
  (total, detail) => ({ ht: total.ht + (detail.priceHT || 0), ttc: total.ttc + (detail.priceTTC || 0) }),
  { ht: 0, ttc: 0 }
);

const Totals = ({ label, total, strong = false }) => (
  <div className="flex justify-end items-center gap-8 pt-2 pr-4">
    <span className={`text-[16px] text-[#3771B0] ${strong ? "font-bold" : "font-semibold"}`}>{label}</span>
    <span className="text-[16px] text-[#3771B0] font-semibold">
      HT: <span className="text-[#38485C] font-bold">{formatAmount(total.ht)}</span>
    </span>
    <span className="text-[16px] text-[#3771B0] font-semibold">
      TTC: <span className="text-[#38485C] font-bold">{formatAmount(total.ttc)}</span>
    </span>
  </div>
);

// Invoice lines grouped by category (labour, paint, then parts by type), each section with its own
// totals - on screen only, the PDF keeps its own layout
const InvoiceRecap = ({ details = [] }) => {
  const sections = useMemo(() => {
    const labour = [];
    const peinture = [];
    const partsByType = {};
    details.forEach((detail) => {
      if (isServiceLine(detail)) {
        labour.push(detail);
      } else if (isPeintureLine(detail) || detail.partType === "PEINTURE_INGREDIENTS") {
        peinture.push(detail);
      } else {
        const type = detail.partType || "NONE";
        (partsByType[type] = partsByType[type] || []).push(detail);
      }
    });
    // Parts sections in PART_TYPES order, untyped (older) lines last; empty sections are skipped
    const partSections = [...PART_TYPES.map((type) => type.value), "NONE"]
      .filter((type) => partsByType[type])
      .map((type) => ({ key: type, title: getPartTypeSectionTitle(type), lines: partsByType[type] }));
    return [
      { key: "labour", title: "Main d'œuvre", lines: labour },
      { key: "peinture", title: "Peinture et ingrédients", lines: peinture },
      ...partSections,
    ].filter((section) => section.lines.length > 0);
  }, [details]);

  return (
    <div className="space-y-6">
      {sections.map((section) => (
        <div key={section.key}>
          <h4 className="text-[18px] text-[#043F87] font-semibold mb-2">{section.title}</h4>
          <div className="space-y-2">
            {section.lines.map((detail, index) => (
              <ProductRow key={detail.id ?? index} product={detail} variant="quote" />
            ))}
          </div>
          <Totals label={`Total ${section.title.toLowerCase()}`} total={sumTotals(section.lines)} />
        </div>
      ))}
    </div>
  );
};

export default InvoiceRecap;
